/** Values the dock depends on that are not (yet) supplied by the backend. */

/** Scene 04 walks steps 1..13. */
export const TOTAL_STEPS = 13;

/** Skip is only offered before this step; from here on the measurement runs unskippable. */
export const SKIP_BEFORE_STEP = 4;

/** A headset counts as online while it has beaten within this window. */
export const ONLINE_THRESHOLD_MS = 10_000;

/**
 * Whether the expanded panel shows the headset's live preview frame.
 *
 * Off for now: the preview is not wanted in front of operators yet, and the settings
 * take the full width instead. Off also means the dock never sends start_screencast,
 * so headsets keep sending bare heartbeats rather than streaming frames nobody sees.
 * The receiving side (the preview cache in useHeadsetRealtime, PreviewPane) is left
 * in place, so turning this back on is the whole change.
 */
export const PREVIEW_ENABLED = false;

/** Heartbeat previews larger than this are dropped rather than kept in state. */
export const PREVIEW_MAX_B64_LENGTH = 200_000;

/**
 * How long an optimistic switch value survives when the command produces no vr.state
 * at all — a POST that failed silently, a socket that was down while the answer went
 * out, or a headset that ignored the command. In the normal case the next vr.state
 * clears the override long before this fires.
 *
 * Deliberately NOT ONLINE_THRESHOLD_MS, despite being the same 10s: presence is
 * measured on heartbeats, which arrive every second whether or not anything changes,
 * while this waits on a vr.state, which only arrives when something does. A healthy,
 * beating headset can still drop a command. Tuning one should not tune the other.
 *
 * It has to cover the whole round trip — POST, the hop to the headset, the headset
 * acting, the state coming back over the socket. The 5s it used to be was tight
 * enough that a slow connection could revert the switch mid-flight, which invites a
 * second click and swaps twice.
 */
export const OPTIMISTIC_TIMEOUT_MS = 10_000;

export const STIMULUS_OFF = "Bird";
export const STIMULUS_ON = "Robot";

/**
 * Which switch position a reported `vr.state.stimulus` is, or undefined when the
 * headset has not reported one yet (the caller keeps undefined distinct so an
 * optimistic value is not cleared by a state that says nothing).
 *
 * Matched case-insensitively on purpose. The casing of these values is the
 * headset's, not ours, and a drift between "Robot" and "robot" would otherwise make
 * every flip of the switch silently spring back — the switch would go to Robot,
 * find the reported value never agrees, and revert on the timeout.
 */
export function stimulusIsOn(reported: string | undefined): boolean | undefined {
  if (reported === undefined) return undefined;
  // anything that is not Robot is Bird, so an unknown third value fails to the default
  return reported.trim().toLowerCase() === STIMULUS_ON.toLowerCase();
}
